import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import TprmDashboard from './TprmDashboard';
import VendorRegister from './VendorRegister';


type TabKey = 'dashboard' | 'data';


// Filter params that only make sense on the Data tab; their presence
// auto-flips the active tab so dashboard KPI drill-downs and audit-log
// cross-links land the user in the right place without needing an explicit
// ?tab=data.
const DATA_ONLY_PARAMS = [
  'search',
  'status',
  'risk_tier',
  'service_type',
  'department',
  'owner_oid',
  'annual_cost_range',
  'contract_type',
  'next_review_before',
  'has_pii',
  'mfa_supported',
  'sso_supported',
  'is_critical_operations',
  'handles_payment',
  'overdue_review_only',
] as const;


export default function TprmPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: TabKey = useMemo(() => {
    const explicit = searchParams.get('tab');
    if (explicit === 'data') return 'data';
    if (explicit === 'dashboard') return 'dashboard';
    if (DATA_ONLY_PARAMS.some((p) => searchParams.get(p))) return 'data';
    return 'dashboard';
  }, [searchParams]);

  // If we landed here via a drill-down link (DATA_ONLY_PARAMS set but no
  // explicit ?tab=), stamp tab=data into the URL so the activeTab stays
  // on Data even after the user clears every filter.
  useEffect(() => {
    if (
      !searchParams.get('tab') &&
      DATA_ONLY_PARAMS.some((p) => searchParams.get(p))
    ) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'data');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const setTab = useCallback(
    (tab: TabKey) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', tab);
      if (tab === 'dashboard') {
        DATA_ONLY_PARAMS.forEach((p) => next.delete(p));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return (
    <div className="h-full w-full">
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 px-6 pt-6">
        {(
          [
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'data', label: 'Data' },
          ] as const
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === t.id
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' ? (
        <TprmDashboard embedded />
      ) : (
        <VendorRegister embedded />
      )}
    </div>
  );
}
